import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import {
  isFieldArrayError,
  isFieldError,
  isNameError,
} from "../../../../../helpers/input-validations";
import CommonSlider from "../../../../common/CommonSlider";
import {
  alcoholEnum,
  caffeineEnum,
  exerciseEnum,
  exerciseTypeEnum,
  recreationalDrugsEnum,
  smokingEnum,
} from "../../../clinical-services/service-steps/clinical-assessment/data";
import {createLifestyle, updateLifestyle, upsertSmokingAlert, removeSmokingAlert} from "../../../../../redux/modules/patient/actions";

export default function LifestyleFactorCreate({
  open,
  onClose,
  selectedLifestyle: existingLifestyle,
  patientId,
  create,
  onSave = () => {},
  onSuccess = () => {},
}) {
  //State Variables
  const dispatch = useDispatch();

  const [selectedLifestyle, setSelectedLifestyle] = useState(null);
  const [alcohol, setAlcohol] = useState(
    create ? null : existingLifestyle?.alcohol || null
  );
  const [caffeine, setCaffeine] = useState(
    create ? null : existingLifestyle?.caffeine || null
  );
  const [smoking, setSmoking] = useState(
    create ? null : existingLifestyle?.smoking || null
  );
  // Helper to normalize array values (handles string, array, null, undefined)
  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      // Handle comma-separated strings
      return value.split(',').map(v => v.trim()).filter(Boolean);
    }
    return value ? [value] : [];
  };

  const [drugs, setDrugs] = useState(
    create ? [] : normalizeArray(existingLifestyle?.recreationalDrugs)
  );
  const [exercise, setExercise] = useState(
    create ? null : existingLifestyle?.exercise || null
  );
  const [exerciseTypes, setExerciseTypes] = useState(
    create ? [] : normalizeArray(existingLifestyle?.exerciseTypes)
  );
  const [comments, setComments] = useState("");
  const [recreationalDrugsOther, setRecreationalDrugsOther] = useState("");
  const [serviceType, setServiceType] = useState(null);
  const [anyError, setAnyError] = useState(false);

  useEffect(() => {
    setSelectedLifestyle(existingLifestyle);
    setAlcohol(create ? null : existingLifestyle?.alcohol || null);
    setCaffeine(create ? null : existingLifestyle?.caffeine || null);
    setSmoking(create ? null : existingLifestyle?.smoking || null);
    setDrugs(create ? [] : normalizeArray(existingLifestyle?.recreationalDrugs));
    setExercise(create ? null : existingLifestyle?.exercise || null);
    setExerciseTypes(create ? [] : normalizeArray(existingLifestyle?.exerciseTypes));
    setComments(create ? "" : existingLifestyle?.comments || "");
    setRecreationalDrugsOther(create ? "" : existingLifestyle?.recreationalDrugsOther || "");
    setServiceType(create ? null : existingLifestyle?.serviceType || null);
  }, [open, existingLifestyle, create]);

  // Clear recreationalDrugsOther when "Other" is deselected
  useEffect(() => {
    if (!hasOtherDrug(drugs)) {
      setRecreationalDrugsOther("");
      setRecreationalDrugsOtherError(false);
    }
  }, [drugs]);

  const [clicked, setClicked] = useState(false);
  const [alcoholError, setAlcoholError] = useState(false);
  const [caffeineError, setCaffeineError] = useState(false);
  const [smokingError, setSmokingError] = useState(false);
  const [drugsError, setDrugsError] = useState(false);
  const [recreationalDrugsOtherError, setRecreationalDrugsOtherError] = useState(false);
  const [exerciseError, setExerciseError] = useState(false);
  const [exerciseTypesError, setExerciseTypesError] = useState(false);

  // Helper function to check if "Other" is selected in recreational drugs
  const hasOtherDrug = (drugsArray) => {
    return Array.isArray(drugsArray) && drugsArray.some(
      (drug) => drug?.toLowerCase() === "other" || drug === "Other"
    );
  };

  const toValueArray = (collection = []) => {
    // Ensure collection is always an array before using array methods
    const arrayCollection = Array.isArray(collection) 
      ? collection 
      : (collection ? [collection] : []);
    return arrayCollection
      .map((item) => (typeof item === "string" ? item : item?.value))
      .filter(Boolean);
  };

  const normalizeMultiSelectWithNone = (
    previousSelection = [],
    nextSelection = [],
    noneValue = "None"
  ) => {
    const previousValues = toValueArray(previousSelection);
    const nextValues = Array.from(new Set(toValueArray(nextSelection)));
    const prevHasNone = previousValues.includes(noneValue);

    if (!nextValues.length) {
      return [];
    }

    if (!nextValues.includes(noneValue)) {
      return nextValues;
    }

    if (!prevHasNone) {
      return [noneValue];
    }

    const otherValues = nextValues.filter((value) => value !== noneValue);
    return otherValues.length > 0 ? otherValues : [noneValue];
  };

  const fieldsToCheck = [
    {
      field: alcohol,
      checkFn: isFieldError,
      setErrorFn: setAlcoholError,
      alter: false,
      name: "alcohol",
    },
    {
      field: caffeine,
      checkFn: isFieldError,
      setErrorFn: setCaffeineError,
      alter: false,
      name: "caffeine",
    },
    {
      field: smoking,
      checkFn: isFieldError,
      setErrorFn: setSmokingError,
      alter: false,
      name: "smoking",
    },
    {
      field: drugs,
      checkFn: isFieldArrayError,
      setErrorFn: setDrugsError,
      alter: false,
      name: "drugs",
    },
    {
      field: exercise,
      checkFn: isFieldError,
      setErrorFn: setExerciseError,
      alter: false,
      name: "exercise",
    },
    {
      field: exerciseTypes,
      checkFn: isFieldArrayError,
      setErrorFn: setExerciseTypesError,
      alter: false,
      name: "exerciseTypes",
    },
  ];

  const resetForm = () => {
    setSelectedLifestyle(null);
    setClicked(false);
    setAlcohol(null);
    setCaffeine(null);
    setSmoking(null);
    setDrugs([]);
    setExercise(null);
    setExerciseTypes([]);
    setComments("");
    setRecreationalDrugsOther("");
    setServiceType(null);

    setAlcoholError(false);
    setCaffeineError(false);
    setSmokingError(false);
    setDrugsError(false);
    setRecreationalDrugsOtherError(false);
    setExerciseError(false);
    setExerciseTypesError(false);
  };

  const manageStep = () => {
    setClicked(true);
    let allFieldsValid = true;

    fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter, name }) => {
      if (name === "exerciseTypes" && exercise === "Never") {
        setErrorFn(false);
        return;
      }

      const hasError = checkFn(field);
      setErrorFn(hasError);

      if (hasError) {
        allFieldsValid = false;
      }
    });

    // Check if "Other" is selected in recreational drugs and validate text
    const isOtherSelected = hasOtherDrug(drugs);
    const isOtherDrugTextValid = isOtherSelected 
      ? recreationalDrugsOther && recreationalDrugsOther.trim() !== "" 
      : true;
    
    setRecreationalDrugsOtherError(isOtherSelected && !isOtherDrugTextValid);
    if (!isOtherDrugTextValid) {
      allFieldsValid = false;
    }

    if (allFieldsValid) {
      // When creating, don't include id, historyId, or other existing record properties
      // When updating, include the existing record properties
      let updatedLifeStyle = create
        ? {
            // Create new record - only include the form fields
            alcohol: alcohol,
            caffeine: caffeine,
            smoking: smoking,
            recreationalDrugs: drugs,
            recreationalDrugsOther: hasOtherDrug(drugs) && recreationalDrugsOther && recreationalDrugsOther.trim() !== "" 
              ? recreationalDrugsOther.trim() 
              : null,
            exercise: exercise,
            exerciseTypes: exerciseTypes,
            comments: comments || "",
            serviceType: serviceType || "Patient Chart", // Default to "Patient Chart" if not specified
            lastLifeStyleDate: new Date(),
          }
        : {
            // Update existing record - include existing properties and update fields
            ...selectedLifestyle,
            alcohol: alcohol,
            caffeine: caffeine,
            smoking: smoking,
            recreationalDrugs: drugs,
            recreationalDrugsOther: hasOtherDrug(drugs) 
              ? (recreationalDrugsOther && recreationalDrugsOther.trim() !== "" 
                  ? recreationalDrugsOther.trim() 
                  : selectedLifestyle?.recreationalDrugsOther || null)
              : null,
            exercise: exercise,
            exerciseTypes: exerciseTypes,
            comments: comments || "",
            serviceType: serviceType || selectedLifestyle?.serviceType || "Patient Chart",
            lastLifeStyleDate: new Date(),
          };
      
      const syncSmokingAlert = () => {
        if (!patientId) return;
        if (smoking && smoking !== "Never") {
          dispatch(upsertSmokingAlert(patientId, smoking));
        } else {
          dispatch(removeSmokingAlert(patientId));
        }
      };

      if (create) {
        dispatch(createLifestyle(patientId, updatedLifeStyle))
          .then((response) => {
            resetForm();
            onClose();
            onSave();
            onSuccess();
            syncSmokingAlert();
          })
          .catch((error) => {
            console.error('Create failed:', error);
            setClicked(false);
          });
      } else {
        dispatch(updateLifestyle(patientId, updatedLifeStyle))
          .then(() => {
            resetForm();
            onClose();
            onSave();
            onSuccess();
            syncSmokingAlert();
          })
          .catch((error) => {
            console.error('Update failed:', error);
            setClicked(false);
          });
      }
    } else {
      setClicked(true);
    }
  };

  useEffect(() => {
    if (clicked) {
      fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
        const hasError = checkFn(field);
        setErrorFn(hasError);
      });

      // Validate recreational drugs "Other" text
      const isOtherSelected = hasOtherDrug(drugs);
      if (isOtherSelected) {
        const isOtherTextValid = recreationalDrugsOther && recreationalDrugsOther.trim() !== "";
        setRecreationalDrugsOtherError(!isOtherTextValid);
      } else {
        setRecreationalDrugsOtherError(false);
      }
    }
  }, [clicked, ...fieldsToCheck.map(({ field }) => field), drugs, recreationalDrugsOther]);

  return (
    <CommonSlider open={open} onClose={onClose}>
      <div className="flex flex-col w-full h-full">
        {/* Header */}
        <div className="px-8 py-6 bg-gray-20 flex-shrink-0">
          <div className="flex items-start justify-between space-x-3">
            <div className="flex space-x-1">
              <h2 className="font-bold text-gray-800 text-display-xs">
                {create ? "Add" : "Update"} Lifestyle Factors
              </h2>
            </div>
            <div className="flex items-center h-7">
              <button
                type="button"
                className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                onClick={() => onClose()}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto w-full">
          <div className="px-8 py-6">
            <div className="grid grid-cols-12 gap-6">
              <CustomInput
                options={alcoholEnum.options.map((x) => {
                  return {
                    label: x.header,
                    value: x.value,
                    subLabel: x.subHeader,
                  };
                })}
                name="alcohol"
                className="col-span-12"
                value={alcohol}
                label="Alcohol Intake"
                type="dropdown"
                onChange={(e) => setAlcohol(e.value)}
                error={alcoholError}
                required
              />

              <CustomInput
                options={caffeineEnum.options.map((x) => {
                  return { label: x.header, value: x.value };
                })}
                name="caffeine"
                className="col-span-12"
                value={caffeine}
                label="Caffeine Intake"
                type="dropdown"
                onChange={(e) => setCaffeine(e.value)}
                error={caffeineError}
                required
              />

              <CustomInput
                options={smokingEnum.options.map((x) => {
                  return {
                    label: x.header,
                    value: x.value,
                    subLabel: x.subHeader,
                  };
                })}
                name="smoking"
                className="col-span-12"
                value={smoking}
                label="Smoking Intake"
                type="dropdown"
                onChange={(e) => setSmoking(e.value)}
                error={smokingError}
                required
              />

              <CustomInput
                options={recreationalDrugsEnum.options.map((x) => {
                  return { label: x.header, value: x.value };
                })}
                name="drugs"
                className="col-span-12"
                value={drugs}
                label="Recreational Drugs"
                type="multi-dropdown"
                onChange={(selected) =>
                  setDrugs(
                    normalizeMultiSelectWithNone(drugs, selected, "None")
                  )
                }
                error={drugsError}
                required
              />

              {/* Show "Other" text input when "Other" is selected in recreational drugs */}
              {hasOtherDrug(drugs) && (
                <div className="col-span-12">
                  <CustomInput
                    name="recreationalDrugsOther"
                    className="col-span-12"
                    label="Other Recreational Drug"
                    placeholder="Enter other recreational drug"
                    value={recreationalDrugsOther}
                    onChange={(e) => setRecreationalDrugsOther(e.target.value)}
                    type="text"
                    error={recreationalDrugsOtherError}
                    required={true}
                  />
                  {recreationalDrugsOtherError && (
                    <span className="text-red-200 text-xs">Other Recreational Drug is required</span>
                  )}
                </div>
              )}

              <CustomInput
                options={exerciseEnum.options.map((x) => {
                  return { label: x.header, value: x.value };
                })}
                name="exercise"
                className="col-span-12"
                value={exercise}
                label="Exercise"
                type="dropdown"
                onChange={(e) => {
                  setExercise(e.value);
                  if (e.value === "Never") {
                    setExerciseTypes([]);
                    setExerciseTypesError(false);
                  }
                }}
                error={exerciseError}
                required
              />

              <CustomInput
                options={exerciseTypeEnum.options.map((x) => {
                  return { label: x.header, value: x.value };
                })}
                name="exerciseTypes"
                className="col-span-12"
                value={exerciseTypes}
                label="Exercise Types"
                type="multi-dropdown"
                onChange={(selected) =>
                  setExerciseTypes(
                    normalizeMultiSelectWithNone(exerciseTypes, selected, "None")
                  )
                }
                error={exerciseTypesError}
                disabled={exercise === "Never"}
                required={exercise !== "Never"}
              />
            </div>
          </div>

          <div className="px-8 py-4">
            <CustomInput
              name="comments"
              className="col-span-12 h-20"
              label="Comments / Description"
              placeholder="Comments / Description"
              value={comments || ""}
              onChange={(e) => setComments(e.target.value || "")}
              type="textarea"
            />
          </div>
        </div>

        {/* Fixed action buttons */}
        <div className="flex items-center justify-center py-6 bg-white border-t border-gray-100 flex-shrink-0 w-full">
          <div className="flex items-center justify-center space-x-5">
            <Button
              className="min-w-[200px]"
              text="Cancel"
              type="secondary"
              onClick={() => {
                resetForm();
                onClose();
              }}
            />
            <Button
              className="min-w-[200px]"
              text={create ? "Save" : "Update"}
              type="primary"
              onClick={() => {
                manageStep();
              }}
            />
          </div>
        </div>
      </div>
    </CommonSlider>
  );
}
